import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { hostThemeMeta, normalizeHostTheme } from "../../lib/hostThemes";
import { upcomingEventsOnly } from "../../lib/publicListingFormat";
import HostProfileView from "./HostProfileView";
import RightDrawer, { RightDrawerBody } from "../ui/RightDrawer";

const SKELETON = "rounded-md2 bg-black/[0.06]";

/**
 * Lu.ma-style right drawer: live preview of the public host profile under a draft theme.
 */
export default function ThemePreviewDrawer({
  open,
  onClose,
  organizerId,
  themeId,
  fallbackHost = null,
}) {
  const theme = normalizeHostTheme(themeId);
  const meta = hostThemeMeta(theme);

  const [host, setHost] = useState(null);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !organizerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api.get(`/organizers/${organizerId}/public`);
        if (cancelled) return;
        setHost(r.data.organizer);
        setSchools(r.data.schools || []);
        setClasses(r.data.classes || []);
        setEvents(upcomingEventsOnly(r.data.events || []));
      } catch {
        if (cancelled) return;
        setError("load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, organizerId]);

  const previewHost = host || fallbackHost;

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      tier="compact"
      ariaLabel={`${meta.name} profile preview`}
      testId="theme-preview-drawer"
      closeTestId="theme-preview-close"
      backdropTestId="theme-preview-backdrop"
      hostTheme={theme}
      hostThemeDark={meta.dark}
    >
      <RightDrawerBody>
        {loading && !previewHost ? (
          <div className="p-4 space-y-4 animate-pulse motion-reduce:animate-none" aria-busy="true">
            <div className={`h-40 rounded-card ${SKELETON}`} />
            <div className={`h-28 rounded-card ${SKELETON}`} />
            <div className={`h-36 rounded-card ${SKELETON}`} />
            <p className="sr-only">Loading preview…</p>
          </div>
        ) : error && !previewHost ? (
          <div className="p-6 text-center">
            <p className="font-ui font-semibold text-[15px] text-ink">Couldn’t load preview</p>
            <p className="mt-1 font-ui text-[13px] text-ink-secondary">
              Save your host profile, then try again.
            </p>
          </div>
        ) : previewHost ? (
          <HostProfileView
            host={previewHost}
            schools={schools}
            classes={classes}
            events={events}
            theme={theme}
            interactive={false}
            testIdPrefix="theme-preview"
            className="pb-8"
          />
        ) : null}
      </RightDrawerBody>
    </RightDrawer>
  );
}
